/**
 * The bell of the identity chrome and the dialog that holds the session inbox's list
 * (`foundation/inbox/session-inbox.tsx`), in the page's mode (`foundation/theme/`; it is under `<html>`, so it needs
 * no attribute of its own).
 */
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { formatCount } from './count';
import './notice-inbox.scss';

function CloseMark() {
  return <svg aria-hidden="true" fill="none" focusable="false" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18" /></svg>;
}

export interface NoticeInboxProps {
  /** The session inbox; without a session there is only the bell. */
  readonly content?: ReactNode;
  readonly knownUnreadCount?: number | null;
  /** False where the shell names the bell itself (its icon rail), so two labels never show at once. */
  readonly tooltip?: boolean;
}

export function NoticeInbox({ knownUnreadCount, content, tooltip = true }: NoticeInboxProps) {
  const [open, setOpen] = useState(false);
  const unreadCount = knownUnreadCount ?? 0;

  const trigger = (
    <button
      aria-label="Mis avisos"
      className="identity-chrome__notice-button"
      data-unread={unreadCount > 0}
      {...(tooltip ? { title: 'Mis avisos' } : {})}
      type="button"
    >
      <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
        <path d="M10.268 21a2 2 0 0 0 3.464 0" />
        <path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326" />
      </svg>
      {unreadCount > 0 && <span className="identity-chrome__notice-count">{formatCount(unreadCount) ?? unreadCount}</span>}
    </button>
  );
  if (content === undefined) return trigger;

  return (
    <Dialog.Root modal={false} onOpenChange={setOpen} open={open}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Content className="notice-inbox notice-inbox--custom">
          <Dialog.Title hidden>Avisos</Dialog.Title>
          <Dialog.Description hidden>Tu buzón personal de avisos.</Dialog.Description>
          <Dialog.Close asChild>
            <button aria-label="Cerrar los avisos" className="notice-inbox__close" type="button"><CloseMark /></button>
          </Dialog.Close>
          {content}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
